import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';

import { useCreateImageMutation } from '../../api/image';
import {
    failImage, getImageByStatus, ImageOut, queueImage, removeImage, updateImageProg
} from '../../Store/imageQueueSlice';
import { RootState } from '../../Store/store';

const UPLOAD_SIZE_LIMIT = 5 * Math.pow(1027, 2);

interface ImageUploadSettings {
  isTma: boolean;
  estCoreDiameterInMm?: number;
}

const useImageUploadService = () => {
  const dispatch = useDispatch();
  const [createImage] = useCreateImageMutation();
  const imageQueue = useSelector((state: RootState) => getImageByStatus(state, "queue"));
  const imageInProg = useSelector((state: RootState) => getImageByStatus(state, "inprog"));

  const uploadImage = async (img: ImageOut, settings: ImageUploadSettings) => {
    dispatch(queueImage(img));
    try {
      let file = await fetch(img.blobUrl)
        .then((r) => r.blob())
        .then((blobFile) => new File([blobFile], img.name, { type: img.type }));

      const fileName = file.name.split(".")[0];
      const fileExtension = file.name.split(".")[1];

      const response = await createImage({
        projectId: img.projectId,
        fileName: `${fileName}.${fileExtension}`,
        name: file.name || fileName,
        isTma: settings.isTma,
        estCoreDiameterInMm: settings.estCoreDiameterInMm,
      }).unwrap();

      const { signed_url, image_id } = response;
      const uploading = { ...img, serverId: image_id };

      await axios.put(signed_url, file, {
        headers: {
          "Content-Type": file.type,
        },
        onUploadProgress: function ({ loaded, total, progress, bytes, estimated, rate, upload = true }) {
          if (!total) total = loaded * 100;
          let percentComplete = loaded / total;
          percentComplete = Math.trunc(percentComplete * 100);
          dispatch(updateImageProg({ ...uploading, progress: percentComplete }));
        },
      });

      // axios does not guarantee a final loaded === total event; without this the
      // item can sit in "inprog" forever and never be counted as awaiting a row
      dispatch(updateImageProg({ ...uploading, progress: 100 }));
    } catch (error: any) {
      console.error(error);
      const message = error?.data?.message || error?.message || "Upload failed";
      dispatch(failImage({ imageId: img.imageId, errorMsg: message }));
    }
  };

  return { uploadImage };
};

export default useImageUploadService;
